import { useEffect, useState } from "react";
import { Camera, MapPin, Bell, X, ShieldCheck } from "lucide-react";

const COPY = {
  camera: {
    icon: Camera,
    title: "Camera access",
    body: "Habano.app uses your camera to scan QR codes at businesses and vendors and to upload photos. We never record video and you can revoke access in your device settings at any time.",
    cta: "Continue · open camera",
  },
  geolocation: {
    icon: MapPin,
    title: "Location access",
    body: "Habano.app uses your location to show nearby places and to help you request a ride with your pickup point. Your location is only sent when you explicitly request a ride.",
    cta: "Continue · share location",
  },
  notifications: {
    icon: Bell,
    title: "Notifications",
    body: "Habano.app sends notifications for review reminders, vendor reactivation, and friend requests. You can control these in Settings · Notifications at any time.",
    cta: "Continue · enable notifications",
  },
};

/**
 * usePermissionPrimer({ kind, onGranted })
 * Returns { ask } — call ask() to show our explanation card, then call the
 * native permission API after the user accepts. Persists "seen" flag so we
 * don't show the primer twice for granted permissions.
 */
export function usePermissionPrimer() {
  const [primer, setPrimer] = useState(null);

  const ask = (kind, run) => {
    const seenKey = `habano.permission.${kind}.acknowledged`;
    if (localStorage.getItem(seenKey) === "1") {
      run();
      return;
    }
    setPrimer({ kind, run, seenKey });
  };

  const PrimerNode = primer ? (
    <Primer
      copy={COPY[primer.kind]}
      onContinue={() => {
        localStorage.setItem(primer.seenKey, "1");
        const r = primer.run; setPrimer(null); r();
      }}
      onCancel={() => setPrimer(null)}
    />
  ) : null;

  return { ask, PrimerNode };
}

function Primer({ copy, onContinue, onCancel }) {
  const Icon = copy.icon;
  useEffect(() => {
    const orig = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = orig; };
  }, []);
  return (
    <div className="fixed inset-0 z-[60] bg-background/95 backdrop-blur grid place-items-center px-6" data-testid={`permission-primer-${copy.title.toLowerCase().split(" ")[0]}`}>
      <div className="lux-card max-w-md w-full p-6 relative">
        <button onClick={onCancel} data-testid="permission-primer-close" className="absolute top-3 right-3 p-2 text-foreground/45 hover:text-foreground"><X className="w-4 h-4" strokeWidth={1.5}/></button>
        <div className="grid place-items-center">
          <span className="w-14 h-14 grid place-items-center rounded-full border border-gold-strong text-gold bg-gold/10">
            <Icon className="w-6 h-6" strokeWidth={1.5}/>
          </span>
        </div>
        <h2 className="font-display text-2xl text-center mt-5 text-foreground">{copy.title}</h2>
        <p className="text-sm text-foreground/65 text-center mt-3 leading-relaxed">{copy.body}</p>
        <div className="mt-5 text-[11px] text-foreground/45 flex items-center justify-center gap-1.5">
          <ShieldCheck className="w-3 h-3" strokeWidth={2}/> You can change this any time in your device settings.
        </div>
        <div className="flex gap-2 mt-6">
          <button onClick={onCancel} className="btn-outline py-2 px-4 text-xs flex-1" data-testid="permission-primer-cancel">Not now</button>
          <button onClick={onContinue} className="btn-gold py-2 px-4 text-xs flex-1" data-testid="permission-primer-continue">{copy.cta}</button>
        </div>
      </div>
    </div>
  );
}
